import React, { ReactNode, useEffect } from 'react';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Body, Button, HelperIcon } from '@sato/components';
import { hoursToMilliseconds } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { twMerge } from 'tailwind-merge';
import * as yup from 'yup';

import { HookFormFileInput } from '@components/ReactHookFormFields/HookFormFileInput';
import { t } from '@stores/localization';
import { MonitoringEvent, monitoringUtils } from '@utils/monitoringUtils';

export interface IncidentReportFields {
  title: string;
  description?: string;
  files?: File[];
}

export interface IncidentFormWrapperProps<T extends object> {
  /** Unique identifier for the type of incident */
  incidentKind: string;
  onSubmit: (values: T) => Promise<unknown>;
  /**
   * Initial values for the form. `undefined` values are allowed so that required fields can be
   * initially empty.
   */
  defaultValues?: DefaultValues<T>;
  /** Like `defaultValues`, but form values will change if this prop changes */
  values?: T;
  /** Validation schema for the form */
  validationSchema: yup.ObjectSchema<T>;
  children?: ReactNode;
  className?: string;
  /** Displays a file upload field if enabled */
  withFileUpload?: boolean;
}

/** A common form wrapper for all incident forms */
export const IncidentFormWrapper = observer(
  <T extends object>({
    incidentKind,
    withFileUpload,
    children,
    className,
    onSubmit,
    defaultValues,
    values,
    validationSchema,
  }: IncidentFormWrapperProps<T>) => {
    // Send monitoring signal when the user "starts incident input":
    // (only once for each incident kind in a browser session, until
    // the incident form is submitted, or enough time has elapsed — so that
    // we'd avoid sending multiple spurious signals if the user navigates
    // back and forth on the page or something)
    //
    const formOpenedMonitoringEventConditionKey = `incident-form-opened/${incidentKind}`;

    const formMethods = useForm({
      defaultValues,
      values,
      resolver: yupResolver(validationSchema),
    });

    useEffect(() => {
      monitoringUtils.sendConditionalEvent({
        conditionKey: formOpenedMonitoringEventConditionKey,
        conditionValue: 'true',
        expirationMs: hoursToMilliseconds(2),
        event: MonitoringEvent.INCIDENT_INPUT_STARTED,
      });
    }, []);

    return (
      <FormProvider {...formMethods}>
        <form
          className={twMerge(
            'flex flex-col gap-4 border-t border-black-light pt-5',
            className
          )}
          onSubmit={formMethods.handleSubmit((values) => {
            // Reset "incident input started" monitoring event state, so that
            // if the user opens the form again, we'd consider that a different
            // incident, and send another signal for that:
            monitoringUtils.resetEventConditionForKey(
              formOpenedMonitoringEventConditionKey
            );

            return onSubmit(values as T);
          })}
        >
          {children}

          {withFileUpload && (
            <HookFormFileInput
              accept="application/pdf,image/jpeg,image/png"
              name="files"
              message={t('supported_attachment_files')}
              label={t('choose_files')}
            />
          )}

          <Button type="submit" className="mx-auto mt-5">
            {t('send')}
          </Button>

          <Body className="mx-auto flex justify-center gap-2">
            {t('message_visibility_label')}

            <HelperIcon
              content={t('message_visibility_tooltip')}
              iconSize="sm"
              icon="info24"
              aria-label={t('helper_icon_aria')}
              placement="top right"
            />
          </Body>
        </form>
      </FormProvider>
    );
  }
);
